import { Suspense } from 'react';

import { getCookie } from '@api/HelperAPI';
import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { TFormalOffers } from '@helpers/FormalOffers/formalOffers.types';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { FlexiOfferPage } from './FlexiOfferPage/FlexiOfferPage';
import { getOfferSetType } from './getOfferSetType';
import { LegacyOfferPage } from './LegacyOfferPage';
import { MicroOfferPage } from './MicroOfferPage';
import { RCFOfferPage } from './RCFOfferPage';
import { RecommendedOfferPage } from './RecommendedOfferPage';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { useRestoreOfferConfigurationStep } from '../../../offerFlowMemory/useRestoreOfferConfigurationStep';

export const ApprovedViewDE = () => {
    useRestoreOfferConfigurationStep();

    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<MainContentPending />}>
                <div className="min-h-screen-offset">
                    <ApprovedPage />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

const ApprovedPage = () => {
    const stateKey = useStateKey();
    const { data: offers } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        select: (response) => response.data.formal_offers,
        suspense: true
    });

    if (!offers || offers.length === 0) {
        const message = `ApprovedViewDE: no formal offers found for ${stateKey}`;
        logError(message, { tags: TAGS.DE_LENDING });
        throw new Error(message);
    }

    const offerSetType = getOfferSetType(offers);

    switch (offerSetType) {
        case 'flexi':
            return <FlexiOfferGate formalOffers={offers} />;
        case 'rcf':
            return (
                <div data-testid="approved-page-rcf">
                    <RCFOfferPage offers={offers} />
                </div>
            );
        case 'rbl':
            return (
                <div data-testid="approved-page-rbl">
                    <LegacyOfferPage offerSetType="rbl" />
                </div>
            );
        case 'flexi_rcf':
            return (
                <div data-testid="approved-page-flexi-rcf">
                    <LegacyOfferPage offerSetType="flexi_rcf" />
                </div>
            );
        case 'flexi_rbl':
            return (
                <div data-testid="approved-page-flexi-rbl">
                    <LegacyOfferPage offerSetType="flexi_rbl" />
                </div>
            );
        case 'micro':
            return (
                <div data-testid="approved-page-micro">
                    <MicroOfferPage offers={offers} />
                </div>
            );
        case 'recommended':
            return (
                <div data-testid="approved-page-recommended">
                    <RecommendedOfferPage />
                </div>
            );
        default:
            return (
                <div data-testid="approved-page-fallback">
                    {/* @ts-expect-error — offerSetType is never null here (we throw above if no offers) */}
                    <LegacyOfferPage offerSetType={offerSetType} />
                </div>
            );
    }
};

const SPLIT_TEST_NAME = 'de_offers_v2';

const FlexiOfferGate = ({ formalOffers }: { formalOffers: TFormalOffers }) => {
    const { branch } = useSplitTestBranch({
        test: SPLIT_TEST_NAME,
        suspense: true
    });

    const isV2 = branch === 'INCLUDE' || getCookie('show_v2_offers') === 'true';

    if (isV2) {
        return (
            <div data-testid="approved-page-flexi-v2">
                <FlexiOfferPage formalOffers={formalOffers} />
            </div>
        );
    }

    return (
        <div data-testid="approved-page-flexi">
            <LegacyOfferPage offerSetType="flexi" />
        </div>
    );
};
